import { detectTempo, onsetEnvelope, type OnsetMethod } from './tempoDetect';

/**
 * Following a performance that moves.
 *
 * `detectTempo` reports one number for a clip, which is right for a loop and
 * wrong at both ends of a take that pushes into the chorus and drags out of
 * it: the average is a tempo the performance was at for one moment. What such
 * a take needs is not a better average but the *beats themselves* — where each
 * one actually landed — so that each can be pinned to the grid where it
 * belongs.
 *
 * The tracker is a dynamic programme over the onset envelope (Ellis, 2007).
 * Every frame is scored as "how good a beat would this be": its own onset
 * strength, plus the score of the best previous beat it could follow, less a
 * penalty for the interval between them straying from the expected period.
 * The penalty is on the *log* of the ratio, so an interval a tenth long and one
 * a tenth short cost the same, and the search runs from half the period to
 * twice it, so a beat the drummer left out does not derail the track — it
 * costs one wide step and carries on. Backtracking from the strongest final
 * beat gives the sequence.
 *
 * The expected period is the global estimate: a seed, not a constraint. The
 * penalty is gentle enough that an interval a fifth off the period still
 * costs less than a real onset is worth — a strong ritardando moves that much
 * across a phrase — and firm enough that a stray accent two thirds of the way
 * through a beat does not pull the track onto it. The balance is a number,
 * `TIGHTNESS`, and the tests hold it up: a click track accelerating from 100
 * to 130 is followed to within a hop at every click, and a steady one comes
 * back evenly spaced.
 */

export interface TrackedBeats {
  /** The performance's average tempo, from `detectTempo`. */
  bpm: number;
  confidence: number;
  /** Where each beat landed, in seconds from the start of the audio, in order. */
  beatSec: number[];
}

const HOP = 256;
/** How firmly an interval is held to the expected period: the Ellis α. */
const TIGHTNESS = 10;

/**
 * Beat positions in frames of the envelope, given the expected period.
 *
 * Exposed for the tests, which drive it with envelopes they built by hand.
 */
export function trackBeats(envelope: Float32Array, periodFrames: number): number[] {
  const n = envelope.length;
  if (n === 0 || periodFrames < 2) return [];

  // The envelope less its mean, scaled to a peak of one, so the onset term and
  // the penalty are on one scale whatever the material's level — and so that
  // a frame with nothing on it *costs* a little to call a beat. Without that
  // the floor between onsets is free, and the track would happily carry on
  // laying beats at the expected period after the music has stopped, or step
  // onto the floor beside a real onset that sits a little off the period.
  let mean = 0;
  for (const v of envelope) mean += v;
  mean /= n;
  let peak = 0;
  for (const v of envelope) peak = Math.max(peak, v - mean);
  const scale = peak > 0 ? 1 / peak : 1;
  const strength = (t: number): number => (envelope[t] - mean) * scale;

  const score = new Float64Array(n);
  const from = new Int32Array(n).fill(-1);
  const minGap = Math.max(1, Math.round(periodFrames / 2));
  const maxGap = Math.round(periodFrames * 2);

  for (let t = 0; t < n; t++) {
    let best = 0;
    let bestFrom = -1;
    for (let gap = minGap; gap <= maxGap; gap++) {
      const previous = t - gap;
      if (previous < 0) break;
      const ratio = Math.log(gap / periodFrames);
      const candidate = score[previous] - TIGHTNESS * ratio * ratio;
      if (candidate > best) {
        best = candidate;
        bestFrom = previous;
      }
    }
    score[t] = strength(t) + best;
    from[t] = bestFrom;
  }

  // The best beat in the last period is where the track ends: later frames
  // may score less only because the audio stopped, not because they were bad.
  let end = n - 1;
  for (let t = Math.max(0, n - maxGap); t < n; t++) {
    if (score[t] > score[end]) end = t;
  }
  const beats: number[] = [];
  for (let t = end; t >= 0; t = from[t]) {
    beats.push(t);
    if (from[t] === -1) break;
  }
  beats.reverse();
  // Beats laid on the floor after the last onset are the track coasting
  // through the period window it had to end in, not beats of the music.
  while (beats.length > 0 && strength(beats[beats.length - 1]) <= 0) beats.pop();
  return beats;
}

/** Blocks the attack is looked for in, either side of where the envelope put the beat. */
const REFINE_BEFORE_SEC = 0.02;
const REFINE_AFTER_SEC = 0.025;
const REFINE_BLOCK_SEC = 0.001;

/**
 * The moment the audio actually rises, near where the envelope said.
 *
 * The onset envelope sees an attack up to a frame early — its analysis window
 * reaches ahead of the hop it is stamped with, and a rise out of silence is
 * enormous in the log domain the moment the window's edge touches it — so a
 * beat placed on the envelope's peak is a dozen milliseconds off the drum.
 * A marker that pins a beat wants the drum, so the energy in millisecond
 * blocks either side is read and the beat moved to the block where it climbs
 * hardest. Material with no attack there — a pad, a held note — is left where
 * the envelope put it.
 */
export function refineOnset(channels: readonly Float32Array[], sampleRate: number, aroundSec: number): number {
  const length = channels[0]?.length ?? 0;
  const block = Math.max(1, Math.round(REFINE_BLOCK_SEC * sampleRate));
  const from = Math.max(0, Math.round((aroundSec - REFINE_BEFORE_SEC) * sampleRate));
  const to = Math.min(length, Math.round((aroundSec + REFINE_AFTER_SEC) * sampleRate));
  const blocks = Math.floor((to - from) / block);
  if (blocks < 3) return aroundSec;

  const energy = new Float64Array(blocks);
  for (let b = 0; b < blocks; b++) {
    let sum = 0;
    for (const channel of channels) {
      for (let i = from + b * block; i < from + (b + 1) * block; i++) sum += channel[i] * channel[i];
    }
    energy[b] = sum;
  }
  // Each block against the few before it, not just the one: a steady tone's
  // millisecond blocks rise and fall with its own cycle, and only a climb
  // over the recent level is an attack. The climb has to be several times
  // that level, and a fair share of the loudest thing in the window, or the
  // envelope's position stands.
  const lookBack = 5;
  let loudest = 0;
  for (const e of energy) loudest = Math.max(loudest, e);
  let bestRise = 0;
  let bestBlock = -1;
  // Before the first block lies either more audio or, at the very start of
  // the recording, silence — which is a level like any other.
  for (let b = from === 0 ? 0 : 1; b < blocks; b++) {
    let before = 0;
    let counted = 0;
    for (let k = Math.max(0, b - lookBack); k < b; k++) {
      before += energy[k];
      counted += 1;
    }
    before /= Math.max(1, counted);
    const rise = energy[b] - before;
    if (energy[b] >= before * 3 && rise > bestRise) {
      bestRise = rise;
      bestBlock = b;
    }
  }
  if (bestBlock < 0 || bestRise < loudest * 0.1) return aroundSec;
  return (from + bestBlock * block) / sampleRate;
}

/** Where each beat of a recording landed, and the tempo they average to. */
export function detectBeats(
  channels: readonly Float32Array[],
  sampleRate: number,
  options: { method?: OnsetMethod } = {},
): TrackedBeats | null {
  const tempo = detectTempo(channels, sampleRate, { method: options.method });
  if (!tempo) return null;
  const envelope = onsetEnvelope(channels, sampleRate, options.method, HOP);
  const framesPerSec = sampleRate / HOP;
  const periodFrames = (60 / tempo.bpm) * framesPerSec;
  const frames = trackBeats(envelope, periodFrames);
  const beatSec = frames.map((frame) => refineOnset(channels, sampleRate, frame / framesPerSec));
  return { bpm: tempo.bpm, confidence: tempo.confidence, beatSec };
}

/**
 * The tracked beats numbered, and the tempo they make between themselves.
 *
 * Beat numbers come from the intervals — one period apart is one beat, two
 * apart is a beat the drummer left out — starting from wherever the first one
 * falls under the average tempo. The tempo is then what the beats actually
 * make between the first and the last, and the first beat's number is re-read
 * under it. Shared by the two things a tracked performance can become: warp
 * markers that straighten it to the grid, and a tempo map that bends the grid
 * to it.
 */
export function numberBeats(beats: TrackedBeats): { numbers: number[]; bpm: number } | null {
  const { beatSec } = beats;
  if (beatSec.length < 2) return null;
  const nominalPeriod = 60 / beats.bpm;

  const numbers: number[] = [Math.round(beatSec[0] / nominalPeriod)];
  for (let i = 1; i < beatSec.length; i++) {
    const step = Math.max(1, Math.round((beatSec[i] - beatSec[i - 1]) / nominalPeriod));
    numbers.push(numbers[i - 1] + step);
  }

  const span = beatSec[beatSec.length - 1] - beatSec[0];
  const beatsSpanned = numbers[numbers.length - 1] - numbers[0];
  if (span <= 0 || beatsSpanned <= 0) return null;
  const bpm = (60 * beatsSpanned) / span;
  const firstNumber = Math.round(beatSec[0] / (60 / bpm));
  const offset = firstNumber - numbers[0];
  return { numbers: numbers.map((n) => n + offset), bpm };
}

/**
 * Warp markers that pin each tracked beat to the grid.
 *
 * Markers that the curve would drop anyway (outside the clip, or crowding its
 * ends) are left for it to drop.
 */
export function warpMarkersForBeats(
  beats: TrackedBeats,
  makeId: () => string,
): { sourceBpm: number; markers: { id: string; sourceSec: number; beat: number }[] } | null {
  const numbered = numberBeats(beats);
  if (!numbered) return null;
  return {
    sourceBpm: numbered.bpm,
    markers: beats.beatSec.map((sourceSec, i) => ({ id: makeId(), sourceSec, beat: numbered.numbers[i] })),
  };
}
